
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Eye, 
  Heart, 
  MessageCircle, 
  Share2, 
  TrendingUp, 
  Users,
  BarChart3,
  Calendar
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const mockData = [
  { name: 'Mon', views: 1200, likes: 450, comments: 89, shares: 23 },
  { name: 'Tue', views: 1900, likes: 620, comments: 142, shares: 34 },
  { name: 'Wed', views: 1600, likes: 580, comments: 98, shares: 28 },
  { name: 'Thu', views: 2200, likes: 780, comments: 165, shares: 45 },
  { name: 'Fri', views: 2800, likes: 920, comments: 203, shares: 67 },
  { name: 'Sat', views: 3200, likes: 1100, comments: 245, shares: 78 },
  { name: 'Sun', views: 2600, likes: 890, comments: 187, shares: 56 },
];

const platformStats = {
  facebook: { followers: 15420, growth: 12.5, posts: 24, engagement: 8.7 },
  instagram: { followers: 28750, growth: 18.3, posts: 36, engagement: 12.4 },
  tiktok: { followers: 45200, growth: 24.7, posts: 18, engagement: 15.2 },
  youtube: { followers: 8340, growth: 9.8, posts: 8, engagement: 6.3 },
  linkedin: { followers: 5680, growth: 7.2, posts: 12, engagement: 4.8 },
};

export const PlatformOverview = ({ platforms }) => {
  const totalFollowers = Object.values(platformStats).reduce((sum, stat) => sum + stat.followers, 0);
  const avgGrowth = Object.values(platformStats).reduce((sum, stat) => sum + stat.growth, 0) / Object.values(platformStats).length;
  const totalPosts = Object.values(platformStats).reduce((sum, stat) => sum + stat.posts, 0);
  const avgEngagement = Object.values(platformStats).reduce((sum, stat) => sum + stat.engagement, 0) / Object.values(platformStats).length;

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Followers</p>
                <p className="text-2xl font-bold text-blue-900">{totalFollowers.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Avg Growth Rate</p>
                <p className="text-2xl font-bold text-green-900">+{avgGrowth.toFixed(1)}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Total Posts</p>
                <p className="text-2xl font-bold text-purple-900">{totalPosts}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Avg Engagement</p>
                <p className="text-2xl font-bold text-orange-900">{avgEngagement.toFixed(1)}%</p>
              </div>
              <Heart className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Weekly Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={mockData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="views" stroke="#8884d8" fill="url(#colorViews)" />
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Engagement Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={mockData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="likes" fill="#ff6b6b" />
                <Bar dataKey="comments" fill="#4ecdc4" />
                <Bar dataKey="shares" fill="#45b7d1" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.map((platform) => {
          const stats = platformStats[platform.id];
          return (
            <Card key={platform.id} className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${platform.color} flex items-center justify-center`}>
                      {platform.icon && <platform.icon className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{platform.name}</CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        Connected
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Followers</p>
                    <p className="text-xl font-bold">{stats.followers.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Growth</p>
                    <p className="text-xl font-bold text-green-600">+{stats.growth}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Posts</p>
                    <p className="text-xl font-bold">{stats.posts}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Engagement</p>
                    <p className="text-xl font-bold">{stats.engagement}%</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="text-xs">
                    View Insights
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs">
                    Manage Posts
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
